import { useAppDispatch, useAppSelector } from "store";

import classNames from "classnames";
import { useCallback, useEffect, useMemo, useState } from "react";
import { Col, Container, Row } from "react-bootstrap";
import { useDebounce } from "use-debounce";

import { personalDataCitySelector, setSelectedStore, toggleStoreSelectionModal } from "features";
import { TShopStoreByTerritory } from "types";
import StoreSelectedIcon from "image/store-selected.svg";
import shopService from "services/shopService";

import "./StoreSelectionModal.scss";

const StoreSelectionModalForm = () => {
  const [inputValue, setInputValue] = useState("");
  const [isSearchOpen, setIsSearchOpen] = useState(false);
  const [debouncedValue] = useDebounce(inputValue, 300);

  const dispatch = useAppDispatch();
  const selectedStore = useAppSelector((store) => store.uiData.selectedStore);
  const userCity = useAppSelector(personalDataCitySelector);
  const [stores, setStores] = useState<TShopStoreByTerritory[]>([]);

  const searchResults = useMemo(() => {
    if (!debouncedValue.trim()) {
      return [];
    }
    return stores.filter(
      (store) =>
        store.Наименование.toLowerCase().includes(debouncedValue.toLowerCase()) ||
        store.Адрес.toLowerCase().includes(debouncedValue.toLowerCase()) ||
        store.НаселенныйПункт.toLowerCase().includes(debouncedValue.toLowerCase())
    );
  }, [debouncedValue, stores]);

  const handleSelectStore = useCallback(
    (store: TShopStoreByTerritory) => {
      dispatch(setSelectedStore(store));
      localStorage.setItem("selectedStore", JSON.stringify(store));
      dispatch(toggleStoreSelectionModal(false));
      setInputValue("");
      setIsSearchOpen(false);
    },
    [dispatch]
  );

  const handleInputChange = useCallback((value: string) => {
    setInputValue(value);
    setIsSearchOpen(true);
  }, []);

  const handleInputFocus = useCallback(() => {
    setIsSearchOpen(true);
  }, []);

  const handleInputBlur = useCallback(() => {
    setTimeout(() => {
      setIsSearchOpen(false);
    }, 200);
  }, []);

  useEffect(() => {
    const loadStores = async () => {
      const cityCurrent = localStorage.getItem("city");
      const rawКодТерритории = userCity ?? (cityCurrent ? +cityCurrent : 17);
      const КодТерритории = typeof rawКодТерритории === 'string' ? +rawКодТерритории : rawКодТерритории;

      const { data } = await shopService.getStoresByTerritory({
        КодТерритории,
      });
      setStores(data.Data ?? []);
    };

    loadStores();
  }, [userCity]);

  return (
    <Container className="container-store-selection">
      <Row>
        <Col>
          <div className="box-search">
            <input
              type="text"
              className="search-input"
              placeholder="Поиск магазина"
              value={inputValue}
              onChange={(e) => handleInputChange(e.target.value)}
              onFocus={handleInputFocus}
              onBlur={handleInputBlur}
            />
            <button className="button-search"></button>
            {isSearchOpen && searchResults.length > 0 && (
              <div className="search-results">
                {searchResults.map((store) => (
                  <div
                    key={store.Дискаунтер}
                    className="search-result-item"
                    onClick={() => handleSelectStore(store)}
                  >
                    <div className="store-card-name">
                      {store.Адрес}
                      {selectedStore?.Дискаунтер === store.Дискаунтер && (
                        <img
                          src={StoreSelectedIcon}
                          alt="Магазин выбран"
                          className="store-card-selected-icon"
                          width={19}
                          height={13}
                        />
                      )}
                    </div>
                    <div className="wrap-store-tag">
                      {store.ДоступенКаталогСамовывозаИзМагазина && (
                        <div className="store-card-tag green">самовывоз</div>
                      )}
                      {store.ДоступенКаталогДоставкиИзМагазина && (
                        <div className="store-card-tag green">Доставка</div>
                      )}
                      {!store.ДоступенКаталогСамовывозаИзМагазина &&
                        !store.ДоступенКаталогДоставкиИзМагазина && (
                          <div className="store-card-tag">только оффлайн</div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </Col>
      </Row>
      <Row className="stores-grid">
        {stores.map((store) => (
          <Col key={store.Дискаунтер} sm={6} className="store-card-col">
            <div
              className={classNames("store-card", {
                "store-card-selected": selectedStore?.Дискаунтер === store.Дискаунтер,
              })}
              onClick={() => handleSelectStore(store)}
            >
              <div className="store-card-name">
                {store.Адрес}
                {selectedStore?.Дискаунтер === store.Дискаунтер && (
                  <img
                    src={StoreSelectedIcon}
                    alt="Магазин выбран"
                    className="store-card-selected-icon"
                    width={19}
                    height={13}
                  />
                )}
              </div>
              <div className="wrap-store-tag">
                {store.ДоступенКаталогСамовывозаИзМагазина && (
                  <div className="store-card-tag green">самовывоз</div>
                )}
                {store.ДоступенКаталогДоставкиИзМагазина && (
                  <div className="store-card-tag green">Доставка</div>
                )}
                {!store.ДоступенКаталогСамовывозаИзМагазина &&
                  !store.ДоступенКаталогДоставкиИзМагазина && (
                  <div className="store-card-tag">только оффлайн</div>
                )}
              </div>
            </div>
          </Col>
        ))}
      </Row>
    </Container>
  );
};

export default StoreSelectionModalForm;

